/**
 * Login Modal Component
 * Simple modal wrapper for LoginForm component
 */

import React, { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { LoginForm } from './LoginForm';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();

  // Close modal when user successfully authenticates
  useEffect(() => {
    if (isAuthenticated) {
      onClose();
    }
  }, [isAuthenticated, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-8 scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Gradient top accent */}
          <div className="h-1.5 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #dc2626, #ea580c, #f59e0b)' }} />

          <div className="p-6 md:p-8">
          {/* Header with Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Sign in to your account
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Logo and Description */}
          <div className="text-center mb-6">
            <div className="mx-auto h-20 w-20 flex items-center justify-center mb-4">
              <img
                src="/images/header/ap-logo-large.png"
                alt="Andhra Pradesh Government"
                className="w-16 h-16 object-contain"
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter your <span className="font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded text-base">DTCP Portal</span> credentials
            </p>
          </div>

          {/* Login Form */}
          <LoginForm onSuccess={onClose} colorScheme="amber" autoFocus={true} useERPAuth={true} />
          </div>
        </div>
      </div>
    </div>
  );
};
